import { Button, Center, Group, Stack, Table, ActionIcon, Text, Image, Badge, Box } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { useQueryCreatives, useDeleteCreative } from 'hooks/creatives'
import { useAppTranslation } from 'hooks/useTranslation'
import { IconEdit, IconTrash, IconEye } from '@tabler/icons-react'
import { YesNoDialog } from 'components/YesNoDialog'
import { useDisclosure } from '@mantine/hooks'
import { useState } from 'react'

export const Creatives = () => {
  const navigate = useNavigate()
  const { t } = useAppTranslation()
  const [opened, { open, close }] = useDisclosure(false)
  const [deleteId, setDeleteId] = useState('')

  const deleteCreativeMutation = useDeleteCreative(() => {
    close()
  })

  const { isLoading, isError, error, creatives } = useQueryCreatives()

  if (isError) {
    return (
      <div>
        {t('common.error')}
        {error?.message}
      </div>
    )
  }
  if (isLoading) {
    return <div>{t('common.loading')}</div>
  }

  const goToCreateCreative = () => {
    navigate(`${window.location.pathname}/create`)
  }

  const editCreative = (id: string) => {
    navigate(`${window.location.pathname}/${id}`)
  }

  const deleteCreativeDialog = (id: string) => {
    setDeleteId(id)
    open()
  }

  const deleteCreative = () => {
    deleteCreativeMutation.mutate(deleteId)
  }

  const rows = creatives.map((creative) => (
    <Table.Tr
      key={creative.id}
      onClick={() => editCreative(creative.id)}
      style={{ cursor: 'pointer' }}
    >
      <Table.Td>
        {creative.card_image_url && (
          <Image
            src={creative.card_image_url}
            alt={creative.title || 'Creative'}
            h={60}
            w={80}
            fit="cover"
            radius="sm"
          />
        )}
      </Table.Td>
      <Table.Td>
        <Box>
          <Text fw={500}>{creative.title || t('creatives.untitled')}</Text>
          <Text size="sm" c="dimmed">{creative.advertiser.name}</Text>
        </Box>
      </Table.Td>
      <Table.Td>
        {creative.description && (
          <Text size="sm" lineClamp={2}>
            {creative.description}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {creative.cta_text && (
          <Badge variant="light">
            {creative.cta_text}
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            disabled={!creative.card_image_url}
            onClick={(e) => {
              e.stopPropagation()
              if (!creative.card_image_url) return
              window.open(creative.card_image_url, '_blank')
            }}
          >
            <IconEye size="1rem" />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            onClick={(e) => {
              e.stopPropagation()
              editCreative(creative.id)
            }}
          >
            <IconEdit size="1rem" />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            onClick={(e) => {
              e.stopPropagation()
              deleteCreativeDialog(creative.id)
            }}
          >
            <IconTrash size="1rem" />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ))

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Text size="xl" fw="bold">
            {t('creatives.title')}
          </Text>
          <Text c="dimmed">
            {creatives.length} {creatives.length === 1 ? 'creative' : 'creatives'}
          </Text>
        </div>
        <Button onClick={goToCreateCreative}>
          {t('creatives.create_creative')}
        </Button>
      </Group>

      {creatives.length === 0 ? (
        <Center h={200}>
          <Stack align="center">
            <Text c="dimmed">{t('creatives.no_creatives')}</Text>
            <Button onClick={goToCreateCreative}>
              {t('creatives.create_first_creative')}
            </Button>
          </Stack>
        </Center>
      ) : (
        <Table.ScrollContainer minWidth={800}>
          <Table verticalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('creatives.image')}</Table.Th>
                <Table.Th>{t('creatives.title')}</Table.Th>
                <Table.Th>{t('creatives.description')}</Table.Th>
                <Table.Th>{t('creatives.cta_text')}</Table.Th>
                <Table.Th>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <YesNoDialog
        opened={opened}
        onClose={close}
        onYes={deleteCreative}
        title={t('common.delete')}
        question={t('creatives.delete_confirmation')}
      />
    </Stack>
  )
}